import { MaterialIcons } from "@expo/vector-icons";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import DraggableFlatList, {
  DragEndParams,
  RenderItemParams,
  ScaleDecorator,
} from "react-native-draggable-flatlist";
import { useTheme } from "react-native-elements";
import { PlayerContext } from "../../backend/PlayerContext";
import {
  getQuickbarActions,
  QuickbarAction,
  removeFromQuickbar,
} from "../../backend/Quickbar";
import { ActiveMark } from "../utility/ActiveMark";
import { Button } from "../utility/Button";
import { KeyboardListener } from "../utility/KeyboardListener";
import {
  disabledColorByTheme,
  getPrimaryButtonTextColor,
} from "../utility/Miscellaneous";
import { TemporaryEffectsViewer } from "../utility/TemporaryEffectsViewer";
import { Text } from "../utility/Text";

function QuickbarIndexSelector(props: {}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const currentQuickbar = playerContext.global.currentQuickbar;
  const disableBackArrow = currentQuickbar <= 0;
  const disableForwardArrow = currentQuickbar >= 3;
  const slotNumber = currentQuickbar + 1;

  const goBack = useCallback(() => {
    playerContextRef.current.apply((state) => {
      state.global.currentQuickbar = state.global.currentQuickbar - 1;
      return state;
    });
  }, []);

  const goForward = useCallback(() => {
    playerContextRef.current.apply((state) => {
      state.global.currentQuickbar = state.global.currentQuickbar + 1;
      return state;
    });
  }, []);

  const theme = useTheme();

  const disabledIconColor = disabledColorByTheme(theme.theme);

  const backButton = useMemo(
    () => (
      <Button
        disabled={disableBackArrow}
        accessibilityLabel="Previous Quickbar"
        containerStyle={styles.quickbarSelectorButton}
        style={styles.quickbarSelectorButtonInner}
        iconContainerStyle={styles.quickbarSelectorButtonInner}
        buttonStyle={styles.quickbarSelectorButtonInner}
        icon={
          <MaterialIcons
            name="arrow-back"
            size={24}
            color={disableBackArrow ? disabledIconColor : "white"}
          />
        }
        onPress={goBack}
      />
    ),
    [disableBackArrow, disabledIconColor, goBack],
  );
  const forwardButton = useMemo(
    () => (
      <Button
        disabled={disableForwardArrow}
        accessibilityLabel="Next Quickbar"
        containerStyle={styles.quickbarSelectorButton}
        style={styles.quickbarSelectorButtonInner}
        iconContainerStyle={styles.quickbarSelectorButtonInner}
        buttonStyle={styles.quickbarSelectorButtonInner}
        icon={
          <MaterialIcons
            name="arrow-forward"
            size={24}
            color={disableForwardArrow ? disabledIconColor : "white"}
          />
        }
        onPress={goForward}
      />
    ),
    [disableForwardArrow, disabledIconColor, goForward],
  );

  return useMemo(
    () => (
      <View
        style={[
          styles.row,
          {
            alignSelf: "stretch",
            justifyContent: "center",
          },
        ]}
      >
        {backButton}
        <View style={styles.barText}>
          <View>
            <Text>Bar {slotNumber}</Text>
          </View>
          {Platform.OS === "web" && (
            <View>
              <Text
                style={[
                  styles.hotkeyText,
                  theme.theme.mode == "light" && { color: "#088" },
                ]}
                numberOfLines={1}
              >
                [Ctrl+1~4]
              </Text>
            </View>
          )}
        </View>
        {forwardButton}
      </View>
    ),
    [backButton, slotNumber, forwardButton],
  );
}

function QuickbarActionContainer(props: {
  quickbarAction: QuickbarAction;
  hotkey: string | undefined;
  drag: () => void;
}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const quickbarAction = props.quickbarAction;
  const name = quickbarAction.getDisplayName(playerContext);
  const transform = quickbarAction.getTransform(playerContext);
  const data = quickbarAction.toQuickbarData();
  const enabled = quickbarAction.isEnabled(playerContext);
  const active = quickbarAction.isActive(playerContext);

  const theme = useTheme();

  const title = useMemo(() => {
    if (props.hotkey) {
      return (
        <Text
          style={[
            styles.smallButtonTitle,
            {
              color: getPrimaryButtonTextColor(theme.theme, !enabled),
            },
          ]}
          numberOfLines={1}
        >
          <Text
            style={
              enabled
                ? styles.hotkeyText
                : { color: getPrimaryButtonTextColor(theme.theme, !enabled) }
            }
          >
            [{props.hotkey}]
          </Text>{" "}
          {name}
        </Text>
      );
    }
    return name;
  }, [props.hotkey, name, theme, enabled]);

  const onPress = useCallback(
    () => playerContextRef.current.apply(transform),
    [transform],
  );

  return useMemo(
    () => (
      <ScaleDecorator>
        <View style={styles.row}>
          <View
            importantForAccessibility="no-hide-descendants"
            accessibilityElementsHidden={true}
            style={styles.dragHandle}
          >
            <TouchableWithoutFeedback
              onPressIn={props.drag}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              importantForAccessibility="no-hide-descendants"
              accessibilityElementsHidden={true}
            >
              <MaterialIcons
                name="drag-indicator"
                size={24}
                color={theme.theme.colors.grey3}
              />
            </TouchableWithoutFeedback>
          </View>
          <Button
            title={title}
            titleProps={{ numberOfLines: 1 }}
            icon={active ? <ActiveMark disabled={!enabled} /> : undefined}
            containerStyle={styles.mainButton}
            titleStyle={styles.smallButtonTitle}
            onPress={onPress}
            disabled={!enabled}
            enableAutoRepress={true}
          />
          <Button
            icon={<MaterialIcons name="close" size={24} color="white" />}
            accessibilityLabel="Remove from Quickbar"
            // importantForAccessibility="no-hide-descendants"
            // accessibilityElementsHidden={true}
            containerStyle={styles.deleteButton}
            style={styles.deleteButtonInner}
            iconContainerStyle={styles.deleteButtonInner}
            buttonStyle={styles.deleteButtonInner}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            onPress={() =>
              playerContextRef.current.apply((state) => {
                return removeFromQuickbar(state, data);
              })
            }
          />
        </View>
      </ScaleDecorator>
    ),
    [
      name,
      playerContextRef,
      transform,
      enabled,
      active,
      removeFromQuickbar,
      props.hotkey,
      theme,
      props.drag,
    ],
  );
}

export function QuickbarContainer() {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const quickbar = getQuickbarActions(playerContext);
  const quickbarJSON = JSON.stringify(
    quickbar.map((action) => action.toQuickbarData()),
  );

  const renderItem = useCallback(
    ({ item, index, drag, isActive }: RenderItemParams<QuickbarAction>) => (
      <QuickbarActionContainer
        hotkey={
          Platform.OS === "web"
            ? (((index ?? 0) + 1) % 10).toString()
            : undefined
        }
        drag={drag}
        quickbarAction={item}
      />
    ),
    [],
  );
  const keyExtractor = useCallback(
    (quickbarAction: QuickbarAction) =>
      JSON.stringify(quickbarAction.toQuickbarData()),
    [],
  );
  const quickbarKeypressCallback = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.ctrlKey) {
        let slot = -1;
        switch (ev.key) {
          case "1":
            slot = 0;
            break;
          case "2":
            slot = 1;
            break;
          case "3":
            slot = 2;
            break;
          case "4":
            slot = 3;
            break;
        }
        if (slot > -1) {
          playerContextRef.current.apply((state) => {
            state.global.currentQuickbar = slot;
            return state;
          });
        }
        return;
      }
      let slot = -1;
      switch (ev.key) {
        case "1":
          slot = 0;
          break;
        case "2":
          slot = 1;
          break;
        case "3":
          slot = 2;
          break;
        case "4":
          slot = 3;
          break;
        case "5":
          slot = 4;
          break;
        case "6":
          slot = 5;
          break;
        case "7":
          slot = 6;
          break;
        case "8":
          slot = 7;
          break;
        case "9":
          slot = 8;
          break;
        case "0":
          slot = 9;
          break;
      }

      if (slot >= 0) {
        const quickbarAction = quickbar?.[slot];
        if (quickbarAction != null) {
          const transform = quickbarAction.getTransform(
            playerContextRef.current,
          );
          const enabled = quickbarAction.isEnabled(playerContextRef.current);
          if (enabled) {
            playerContextRef.current.apply(transform);
          }
        }
      }
    },
    [quickbarJSON],
  );
  const onDragEnd = useCallback((params: DragEndParams<QuickbarAction>) => {
    playerContextRef.current.apply((state) => {
      state.global.quickbars[state.global.currentQuickbar] = params.data.map(
        (quickbarAction) => quickbarAction.toQuickbarData(),
      );
      return state;
    });
  }, []);
  return useMemo(
    () => (
      <View style={styles.mainContainer}>
        <KeyboardListener callback={quickbarKeypressCallback} />
        <View style={styles.actionsContainer}>
          {Platform.select({
            web: (
              <ScrollView
                style={styles.actionsScroll}
                nestedScrollEnabled={true}
              >
                {quickbar.length > 0 ? (
                  <DraggableFlatList
                    renderItem={renderItem}
                    data={quickbar}
                    keyExtractor={keyExtractor}
                    onDragEnd={onDragEnd}
                    nestedScrollEnabled={true}
                  />
                ) : (
                  <Text style={styles.emptyText}>
                    Your Quickbar is empty. Add actions to the Quickbar through
                    the "Inventory" and "Spells" menus at the bottom!
                  </Text>
                )}
              </ScrollView>
            ),
            default:
              quickbar.length > 0 ? (
                <DraggableFlatList
                  renderItem={renderItem}
                  data={quickbar}
                  keyExtractor={keyExtractor}
                  onDragEnd={onDragEnd}
                  nestedScrollEnabled={true}
                />
              ) : (
                <Text style={styles.emptyText}>
                  Your Quickbar is empty. Add actions to the Quickbar through
                  the "Inventory" and "Spells" menus at the bottom!
                </Text>
              ),
          })}
        </View>
        <View style={styles.infoContainer}>
          <View
            style={styles.accessibilityHidden}
            accessible={true}
            accessibilityLabel="Select Quickbar slot"
            accessibilityRole="header"
            accessibilityElementsHidden={false}
          />
          <QuickbarIndexSelector />
          <ScrollView style={styles.infoContainer} nestedScrollEnabled={true}>
            <TemporaryEffectsViewer accessibilityHeader="Player Buffs" />
          </ScrollView>
        </View>
      </View>
    ),
    [
      renderItem,
      keyExtractor,
      onDragEnd,
      JSON.stringify(quickbar),
      quickbarKeypressCallback,
    ],
  );
}

const styles = StyleSheet.create({
  emptyText: { fontWeight: "bold", fontStyle: "italic" },
  row: { flexDirection: "row", alignItems: "center", marginVertical: 4 },
  mainContainer: {
    flexDirection: "row",
    alignItems: "stretch",
    height: "100%",
  },
  actionsContainer: {
    flex: 1.5,
    marginRight: 8,
  },
  infoContainer: { flex: 1 },
  actionsScroll: { flex: 1 },
  mainButton: {
    flex: 1,
  },
  smallButtonTitle: {
    fontSize: 12,
  },
  deleteButton: {
    marginLeft: 8,
  },
  deleteButtonInner: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    height: 24,
    width: 24,
    borderRadius: 16,
  },
  hotkeyText: {
    color: "#8ff",
  },
  quickbarSelectorButton: {
    height: 32,
    width: 32,
    borderRadius: 16,
    marginHorizontal: 4,
  },
  quickbarSelectorButtonInner: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    height: 32,
  },
  barText: {
    marginHorizontal: 8,
    alignItems: "center",
  },
  dragHandle: {
    cursor: "grab",
    marginRight: 8,
  },
  accessibilityHidden: {
    width: 1,
    height: 1,
    position: "absolute",
    left: 0,
    top: 0,
  },
});
